import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  troubleshootingKeys,
  useParticipantTimeline,
  useParticipantTimelineConfig,
  useTroubleshootingForbidden,
} from "@/hooks/useTroubleshooting";
import {
  TIMELINE_LANES,
  TimelineLaneEnum,
} from "@/domain/troubleshooting.enum";
import {
  ParticipantSearchItem,
  TimelineEvent,
} from "@/domain/troubleshooting.interface";
import {
  formatInBackofficeLondon,
  getBackofficeDisplayTimezone,
} from "@/utils/dateUtils";
import {
  buildTimelineSearch,
  computeTimelineWindow,
  dateInAccountTz,
  filterTimelineEvents,
  isEventFocused,
  isLiveEligible,
  parseTimelineSearchParams,
  shiftDate,
  TimelineTab,
  TimelineUrlState,
  TimelineZoom,
  todayInAccountTz,
  withLiveRule,
} from "@/utils/troubleshooting.utils";
import ParticipantSearch from "./ParticipantSearch";
import TimelineToolbar from "./TimelineToolbar";
import TimelineSwimlane, { TimelineSwimlaneHandle } from "./TimelineSwimlane";
import TimelineEventList from "./TimelineEventList";
import TimelineEventDetailPanel from "./TimelineEventDetailPanel";
import ValidationJourneys from "./ValidationJourneys";
import SessionStatusCard from "./SessionStatusCard";
import GeofenceSyncCard from "./GeofenceSyncCard";
import TimelineAccessDenied from "./TimelineAccessDenied";
import { TimelineLegend } from "./outcome";

const TABS: TimelineTab[] = ["timeline", "journeys"];
const NO_NEW_EVENTS: ReadonlySet<string> = new Set();

/**
 * COYN-1507: participant timeline (AC7–AC9). All screen state — participant,
 * window, filters, tab and the open event — lives in the URL so any view can
 * be shared as a link.
 */
const ParticipantTimeline: React.FC = () => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(
    () => parseTimelineSearchParams(searchParams),
    [searchParams],
  );
  const timelineWindow = useMemo(
    () => computeTimelineWindow(state.date, state.zoom),
    [state.date, state.zoom],
  );

  const update = useCallback(
    (patch: Partial<TimelineUrlState>, replace = false) => {
      setSearchParams(
        (prev) =>
          buildTimelineSearch(
            // Leaving today or switching to Week turns Live off.
            withLiveRule({ ...parseTimelineSearchParams(prev), ...patch }),
          ),
        { replace },
      );
    },
    [setSearchParams],
  );

  const timelineParams = useMemo(
    () => ({
      from: timelineWindow.from,
      to: timelineWindow.to,
      lanes: state.lanes,
      failuresOnly: state.failuresOnly,
    }),
    [timelineWindow, state.lanes, state.failuresOnly],
  );

  // §15: any 403 from the troubleshooting API replaces the screen with a
  // "no access" state; the timeline stops fetching (no retries on 403).
  const forbidden = useTroubleshootingForbidden();

  // §16.1: tunable values from the participantTimeline setting (defaults
  // until it loads — the screen never waits for it).
  const config = useParticipantTimelineConfig();
  const liveIntervalMs = config.liveRefreshSeconds * 1000;

  // Live: only for today in Day zoom (an old `live=1` link is ignored).
  const liveAllowed = isLiveEligible(state);
  const live = state.live && liveAllowed && !forbidden;

  const {
    data: timeline,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    dataUpdatedAt,
    isPlaceholderData,
  } = useParticipantTimeline(state.participantId, timelineParams, {
    enabled: !forbidden,
    live,
    liveIntervalMs,
  });

  // Live: diff each refresh of the same query against the previous result.
  const queryKeyString = JSON.stringify(
    troubleshootingKeys.timeline(state.participantId, timelineParams),
  );
  const previousResult = useRef<{ key: string; ids: Set<string> } | null>(null);
  const [newEventIds, setNewEventIds] =
    useState<ReadonlySet<string>>(NO_NEW_EVENTS);
  const [chipDismissedAt, setChipDismissedAt] = useState(0);
  useEffect(() => {
    if (!timeline || isPlaceholderData) return;
    const ids = new Set(timeline.events.map((e) => e.id));
    const previous = previousResult.current;
    setNewEventIds(
      live && previous && previous.key === queryKeyString
        ? new Set(Array.from(ids).filter((id) => !previous.ids.has(id)))
        : NO_NEW_EVENTS,
    );
    previousResult.current = { key: queryKeyString, ids };
  }, [dataUpdatedAt, queryKeyString, isPlaceholderData, live, timeline]);
  const swimlaneRef = useRef<TimelineSwimlaneHandle>(null);

  const filters = useMemo(
    () => ({ lanes: state.lanes, failuresOnly: state.failuresOnly }),
    [state.lanes, state.failuresOnly],
  );
  const events = useMemo(
    () => filterTimelineEvents(timeline?.events || [], filters),
    [timeline, filters],
  );
  const visibleLanes: TimelineLaneEnum[] =
    state.lanes.length > 0 ? state.lanes : TIMELINE_LANES;
  const newCount =
    chipDismissedAt === dataUpdatedAt
      ? 0
      : events.filter((e) => newEventIds.has(e.id)).length;

  // Coming from a geofence record: open the matching event once.
  const autoFocusedGeofence = useRef("");
  useEffect(() => {
    if (
      !state.geofenceId ||
      state.eventId ||
      !timeline ||
      autoFocusedGeofence.current === state.geofenceId
    ) {
      return;
    }
    const focus = { geofenceId: state.geofenceId };
    const match =
      timeline.events.find(
        (e) => e.source === "GEOFENCE" && isEventFocused(e, focus),
      ) || timeline.events.find((e) => isEventFocused(e, focus));
    if (match) {
      autoFocusedGeofence.current = state.geofenceId;
      update({ eventId: match.id }, true);
    }
  }, [timeline, state.geofenceId, state.eventId, update]);

  if (forbidden) {
    return <TimelineAccessDenied />;
  }

  const handleSelectParticipant = (participant: ParticipantSearchItem) =>
    update({ participantId: participant.id, eventId: "", geofenceId: "" });

  const handleSelectEvent = (event: TimelineEvent) =>
    update({ eventId: event.id }, true);

  const handleFocusEvent = (event: TimelineEvent) => {
    const at = new Date(event.occurredAt).getTime();
    const inWindow = at >= timelineWindow.fromMs && at <= timelineWindow.toMs;
    const passesFilters = filterTimelineEvents([event], filters).length === 1;
    update({
      eventId: event.id,
      tab: "timeline",
      ...(inWindow ? {} : { date: dateInAccountTz(event.occurredAt) }),
      ...(passesFilters ? {} : { lanes: [], failuresOnly: false }),
    });
  };

  const windowLabel =
    state.zoom === "week"
      ? `${dayjs(timelineWindow.days[0]).format("D MMM")} – ${dayjs(
          state.date,
        ).format("D MMM YYYY")}`
      : dayjs(state.date).format("ddd D MMM YYYY");

  const participant = timeline?.participant;
  const tabIndex = Math.max(0, TABS.indexOf(state.tab));

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <ParticipantSearch onSelect={handleSelectParticipant} />
        <Typography variant="body2" color="text.secondary">
          {tt("timezoneNote", { tz: getBackofficeDisplayTimezone() })}
        </Typography>
      </Box>

      {!state.participantId ? (
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary">
              {t("search.emptyState")}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Box sx={{ mb: 2 }} data-testid="timeline-participant-header">
            {participant ? (
              <>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {`${participant.firstName} ${participant.lastName}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {participant.cin
                    ? `${t("header.cin")}: ${participant.cin} · `
                    : ""}
                  {`${t("header.id")}: ${participant.id}`}
                </Typography>
              </>
            ) : isLoading ? (
              <Skeleton variant="text" width={280} height={40} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {`${t("header.id")}: ${state.participantId}`}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              alignItems: "stretch",
              gap: 2,
              mb: 2,
            }}
          >
            <SessionStatusCard
              participantId={state.participantId}
              live={live}
              liveIntervalMs={liveIntervalMs}
            />
            <GeofenceSyncCard
              participantId={state.participantId}
              notReceivedDays={config.deviceEventsNotReceivedDays}
            />
          </Box>

          <Tabs
            value={tabIndex}
            onChange={(_, index: number) => update({ tab: TABS[index] }, true)}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            {TABS.map((tab, index) => (
              <Tab
                key={tab}
                label={tt(`tabs.${tab}`)}
                id={`troubleshooting-tab-${index}`}
                aria-controls={`troubleshooting-tabpanel-${index}`}
              />
            ))}
          </Tabs>

          <Box sx={{ mb: 2 }}>
            <TimelineToolbar
              zoom={state.zoom}
              date={state.date}
              windowLabel={windowLabel}
              lanes={state.lanes}
              failuresOnly={state.failuresOnly}
              isNextDisabled={state.date >= todayInAccountTz()}
              isFetching={isFetching}
              showFilters={state.tab === "timeline"}
              live={live}
              liveAllowed={liveAllowed}
              onLiveChange={(value) => update({ live: value }, true)}
              lastUpdated={
                dataUpdatedAt
                  ? formatInBackofficeLondon(dataUpdatedAt, "HH:mm:ss")
                  : undefined
              }
              newCount={state.tab === "timeline" ? newCount : 0}
              onShowNew={() => {
                setChipDismissedAt(dataUpdatedAt);
                swimlaneRef.current?.scrollToLatest(newEventIds);
              }}
              onZoomChange={(zoom: TimelineZoom) => update({ zoom })}
              onDateChange={(date) => update({ date })}
              onShift={(direction) =>
                update({ date: shiftDate(state.date, state.zoom, direction) })
              }
              onToday={() => update({ date: todayInAccountTz() })}
              onLanesChange={(lanes) => update({ lanes }, true)}
              onFailuresOnlyChange={(failuresOnly) =>
                update({ failuresOnly }, true)
              }
              onRefresh={() => refetch()}
            />
            {live && (
              <Alert
                severity="info"
                sx={{ mt: 1.5 }}
                data-testid="timeline-live-notice"
              >
                {t("live.notice")}
              </Alert>
            )}
          </Box>

          <Box
            role="tabpanel"
            id={`troubleshooting-tabpanel-${tabIndex}`}
            aria-labelledby={`troubleshooting-tab-${tabIndex}`}
          >
            {state.tab === "timeline" && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <TimelineLegend />
                  {timeline && (
                    <Typography variant="body2" color="text.secondary">
                      {tt("timeline.eventCount", { total: events.length })}
                    </Typography>
                  )}
                </Box>

                {isFetching && !isLoading && <LinearProgress sx={{ mb: 1 }} />}

                {timeline?.truncated && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    data-testid="timeline-truncated"
                  >
                    {t("timeline.truncated")}
                  </Alert>
                )}

                {isLoading && (
                  <Box sx={{ my: 2 }}>
                    <LinearProgress />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t("timeline.loading")}
                    </Typography>
                  </Box>
                )}

                {isError && !timeline && (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => refetch()}
                      >
                        {t("timeline.retry")}
                      </Button>
                    }
                  >
                    {(error as { message?: string })?.message ||
                      t("timeline.error")}
                  </Alert>
                )}

                {timeline && events.length === 0 && (
                  <Alert severity="info">
                    {timeline.events.length === 0
                      ? t("timeline.empty")
                      : t("timeline.emptyFiltered")}
                  </Alert>
                )}

                {timeline && events.length > 0 && (
                  <>
                    <TimelineSwimlane
                      ref={swimlaneRef}
                      events={events}
                      window={timelineWindow}
                      zoom={state.zoom}
                      lanes={visibleLanes}
                      selectedEventId={state.eventId}
                      focusGeofenceId={state.geofenceId}
                      onSelect={handleSelectEvent}
                      live={live}
                      newEventIds={newEventIds}
                    />
                    <TimelineEventList
                      events={events}
                      selectedEventId={state.eventId}
                      onSelect={handleSelectEvent}
                      newEventIds={newEventIds}
                      resetKey={queryKeyString}
                    />
                  </>
                )}
              </Box>
            )}

            {state.tab === "journeys" && (
              <ValidationJourneys
                participantId={state.participantId}
                window={timelineWindow}
                focusJourneyId={state.journeyId}
                live={live}
                liveIntervalMs={liveIntervalMs}
                onOpenEvent={(eventId) => update({ eventId }, true)}
              />
            )}
          </Box>

          {state.eventId && (
            <TimelineEventDetailPanel
              participantId={state.participantId}
              eventId={state.eventId}
              onClose={() => update({ eventId: "" }, true)}
              onFocusEvent={handleFocusEvent}
              window={timelineWindow}
              onTraceJourney={(journeyId) =>
                update({ tab: "journeys", journeyId, eventId: "" })
              }
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ParticipantTimeline;
